import { Component, Input, Output, EventEmitter } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { firstContextRecord } from 'src/app/common/bhub-common-module/utils/context-array.util';
import { ViewManager, CalcOperand, CalcCondition, CalcConstraint } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';

@Component({
  selector: 'bhub-calculation-cell',
  standalone: false,
  templateUrl: './calculation-cell.component.html',
  styleUrl: './calculation-cell.component.scss'
})
export class CalculationCellComponent extends BaseCellComponent {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  calculatedValue: number | null = null;
  formattedValue: string = '';
  label: string = '';
  fontSize: number = 16;
  fontWeight: string = 'normal';
  hasOverride: boolean = false;

  private lastStoredValue: number | null = null;

  protected initializeCell(): void {
    this.loadConfig();
    this.calculate();
  }

  protected onCellOrDataChange(): void {
    this.loadConfig();
    this.calculate();
  }

  protected onCellUpdate(): void {
    this.loadConfig();
    this.calculate();
  }

  protected onDataSet(data: any): void {
    // Not used - data comes from context
  }

  override reloadData(): void {
    this.calculate();
  }

  private loadConfig(): void {
    this.label = this.getConfigValue('calcLabel', '');
    this.fontSize = this.getConfigValue('calcFontSize', 16);
    this.fontWeight = this.getConfigValue('calcFontWeight', 'normal');
  }

  private calculate(): void {
    // If there's a manual override from PDF edit mode, use it and skip recalculation
    const overrideValue = this.getConfigValue('pdfOverrideValue', undefined);
    if (overrideValue !== undefined) {
      this.hasOverride = true;
      this.formattedValue = overrideValue;
      this.calculatedValue = parseFloat(overrideValue) || 0;
      return;
    }
    this.hasOverride = false;

    const format: string = this.getConfigValue('calcResultFormat', 'number');
    const decimalPlaces: number = this.getConfigValue('calcDecimalPlaces', 2);

    // Determine which operands to use — conditional or default
    const operands = this.resolveOperands();

    if (!operands || operands.length === 0 || !this.viewManager) {
      this.calculatedValue = null;
      this.formattedValue = '';
      return;
    }

    let result: number | null = null;

    for (const operand of operands) {
      let numValue: number | null;

      if (operand.sourceType === 'value') {
        numValue = operand.literalValue ?? null;
      } else {
        const rawValue = this.getOperandValue(operand);
        if (rawValue === null || rawValue === undefined) {
          continue;
        }
        numValue = this.parseNumericValue(rawValue);
      }

      if (numValue === null) {
        continue;
      }

      if (result === null) {
        // First valid operand initializes the result
        result = operand.operator === '-' ? -numValue : numValue;
      } else {
        switch (operand.operator) {
          case '-': result -= numValue; break;
          case '*': result *= numValue; break;
          case '/': result = numValue !== 0 ? result / numValue : result; break;
          default:  result += numValue; break;
        }
      }
    }

    if (result === null) {
      this.calculatedValue = null;
      this.formattedValue = '';
      return;
    }

    // Apply constraints in order
    const constraints: CalcConstraint[] = this.getConfigValue('calcConstraints', []);
    result = this.applyConstraints(result, constraints);

    this.calculatedValue = result;
    this.formattedValue = this.formatValue(result, format, decimalPlaces);

    // Store result if configured and value actually changed
    this.storeResult(result);
  }

  /**
   * Resolves which operands to use. In conditional mode, checks the condition
   * field value and returns the matching condition's operands. Falls back to
   * the default calcOperands when not conditional or no match is found.
   */
  private resolveOperands(): CalcOperand[] {
    const useConditional = this.getConfigValue('calcUseConditional', false);
    if (!useConditional) {
      return this.getConfigValue('calcOperands', []);
    }

    const conditionCtx = this.getConfigValue('calcConditionContextVariable', '');
    const conditionField = this.getConfigValue('calcConditionFieldName', '');
    const conditions: CalcCondition[] = this.getConfigValue('calcConditions', []);

    if (!conditionCtx || !conditionField || !conditions || conditions.length === 0 || !this.viewManager?.contextData) {
      // Fall back to default operands
      return this.getConfigValue('calcOperands', []);
    }

    // Read the condition value from context
    let contextObj = this.viewManager.contextData[conditionCtx];
    if (Array.isArray(contextObj) && contextObj.length > 0) {
      contextObj = contextObj[0];
    }

    const conditionValue = contextObj?.[conditionField];
    if (conditionValue === null || conditionValue === undefined) {
      return [];
    }

    // Find matching condition (case-insensitive string comparison)
    const conditionStr = String(conditionValue).trim().toLowerCase();
    const match = conditions.find(c => c.conditionValue.trim().toLowerCase() === conditionStr);

    return match ? match.operands : [];
  }

  private storeResult(result: number): void {
    const storeInContext = this.getConfigValue('calcStoreInContext', false);
    if (!storeInContext || !this.viewManager) return;

    // Skip emit if value hasn't changed (prevents infinite loops)
    if (this.lastStoredValue === result) return;
    this.lastStoredValue = result;

    const storeMode: string = this.getConfigValue('calcStoreMode', 'newVariable');

    if (storeMode === 'existingField') {
      // Store into a field of an existing context object
      const contextObject = this.getConfigValue('calcStoreContextObject', '');
      const fieldName = this.getConfigValue('calcStoreFieldName', '');
      if (!contextObject || !fieldName) return;

      const existingData = this.viewManager.contextData[contextObject];
      if (existingData && typeof existingData === 'object' && !Array.isArray(existingData)) {
        // Update the field in-place on the existing context object
        existingData[fieldName] = result;
        // Emit the entire context object so dependent cells refresh
        this.contextDataChange.emit({ [contextObject]: existingData });
      }
    } else {
      // Store as a new context variable
      const contextVariable = this.getConfigValue('calcContextVariable', '');
      const newVarFieldName = this.getConfigValue('calcNewVariableFieldName', '');
      if (!contextVariable || !newVarFieldName) return;
      this.contextDataChange.emit({ [contextVariable]: { [newVarFieldName]: result } });
    }
  }

  private parseNumericValue(raw: any): number | null {
    if (typeof raw === 'number') {
      return isNaN(raw) ? null : raw;
    }
    if (typeof raw === 'string') {
      // Strip currency symbols, commas, spaces, then parse
      const cleaned = raw.replace(/[$€£¥,\s]/g, '');
      if (cleaned === '') return null;
      const num = Number(cleaned);
      return isNaN(num) ? null : num;
    }
    return null;
  }

  private getOperandValue(operand: CalcOperand): any {
    if (!this.viewManager?.contextData || !operand.contextVariable || !operand.fieldName) {
      return null;
    }

    const contextData = this.viewManager.contextData[operand.contextVariable];
    if (!contextData) {
      return null;
    }

    // A list context contributes its first row; a single-record context reads directly.
    const record = firstContextRecord(contextData);
    return record ? record[operand.fieldName] : null;
  }

  private formatValue(value: number, format: string, decimalPlaces: number): string {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        }).format(value);
      case 'percent':
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        }).format(value);
      default:
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        }).format(value);
    }
  }

  private applyConstraints(value: number, constraints: CalcConstraint[]): number {
    if (!constraints || constraints.length === 0) return value;

    for (const c of constraints) {
      switch (c.type) {
        case 'min':
          if (c.value !== undefined) value = Math.max(value, c.value);
          break;
        case 'max':
          if (c.value !== undefined) value = Math.min(value, c.value);
          break;
        case 'abs':
          value = Math.abs(value);
          break;
        case 'round':
          switch (c.roundMode) {
            case 'up': value = Math.ceil(value); break;
            case 'down': value = Math.floor(value); break;
            default: value = Math.round(value); break;
          }
          break;
      }
    }
    return value;
  }
}
